import moment from "moment";

export const normalizeTaskTime = (value) => {
    if (value == null || `${value}`.trim() === "") return "";
    if (Number(`${value}`.trim().split(":")[0]) > 23) return "";
    const parsed = moment(`${value}`.trim(), ["HH:mm", "H:mm", "HH:mm:ss", "H:mm:ss"], true);
    return parsed.isValid() ? parsed.format("HH:mm") : "";
};

export const isTaskTimeOrderValid = (start, end) => {
    if (start == null || `${start}`.trim() === "" || end == null || `${end}`.trim() === "") return true;
    const normalizedStart = normalizeTaskTime(start);
    const normalizedEnd = normalizeTaskTime(end);
    return Boolean(normalizedStart && normalizedEnd && normalizedEnd >= normalizedStart);
};

export const getConfiguredTaskTimes = (task) => ({
    startTime: normalizeTaskTime(task?.TaskStartTime ?? task?.taskStartTime),
    endTime: normalizeTaskTime(task?.TaskEndTime ?? task?.taskEndTime),
});

/**
 * Convert 24-hour time format to 12-hour AM/PM format
 * @param {string} timeString - Time in format "HH:00" (e.g., "19:00") or "Sum Of HH:00" (e.g., "Sum Of 19:00")
 * @returns {string} - Formatted time with AM/PM (e.g., "7 PM" or "Sum Of 7 PM")
 */
/**
 * Convert 24-hour time format to 12-hour AM/PM format
 * @param {string} timeString - Time in format "HH:00" (e.g., "19:00") or "Sum Of HH:00" (e.g., "Sum Of 19:00")
 * @returns {string} - Formatted time with AM/PM (e.g., "7 PM" or "Sum Of 7 PM")
 */
export const convertTo12HourFormat = (timeString) => {
    if (!timeString) return '';

    try {
        // Check if input contains "Sum Of" prefix
        const isSumOfFormat = timeString.includes('Sum Of');

        // Extract time from either "HH:00" or "Sum Of HH:00" format
        const timeMatch = timeString.match(/(\d{2}:\d{2})/);
        if (!timeMatch) {
            return timeString;
        }

        const [hours] = timeMatch[1].split(':').map(Number);

        if (hours < 0 || hours > 23) {
            return timeString;
        }

        let hour12 = hours % 12;
        const period = hours >= 12 ? 'PM' : 'AM';

        // 0 hours should display as 12
        if (hour12 === 0) {
            hour12 = 12;
        }

        const formatted12Hour = `${hour12} ${period}`;

        // Return only the formatted time without "Sum Of" prefix
        return formatted12Hour;
    } catch (error) {
        console.error('Error converting time format:', error);
        return timeString;
    }
};

/**
 * Format hourly sum header with converted time
 * @param {string} headerText - Header text like "Sum Of 00:00"
 * @returns {string} - Formatted header like "Sum Of 12 AM"
 */
export const formatHourlySumHeader = (headerText) => {
    if (!headerText) return '';

    // Extract time from "Sum Of HH:00" format
    const timeMatch = headerText.match(/(\d{2}:\d{2})/);
    if (!timeMatch) return headerText;

    const timeString = timeMatch[1];
    const converted12Hour = convertTo12HourFormat(timeString);

    return headerText.replace(timeString, converted12Hour);
};

/**
 * Convert 24-hour time format with seconds to 12-hour AM/PM format
 * @param {string} timeString - Time in format "HH:mm:ss" (e.g., "15:30:00", "00:30:00")
 * @returns {string} - Formatted time with AM/PM (e.g., "3:30 PM", "12:30 AM")
 */
/**
 * Convert 24-hour time format with seconds to 12-hour AM/PM format
 * @param {string} timeString - Time in format "HH:mm:ss" (e.g., "15:30:00", "00:30:00")
 * @returns {string} - Formatted time with AM/PM (e.g., "3:30 PM", "12:30 AM")
 */
export const convertTimeWithSecondsTo12Hour = (timeString) => {
    if (!timeString) return '';

    try {
        // Extract hours and minutes from "HH:mm:ss" format
        const timeMatch = timeString.match(/(\d{2}):(\d{2}):(\d{2})/);
        if (!timeMatch) {
            return timeString;
        }

        const hours = parseInt(timeMatch[1], 10);
        const minutes = timeMatch[2];

        if (hours < 0 || hours > 23) {
            return timeString;
        }

        let hour12 = hours % 12;
        const period = hours >= 12 ? 'PM' : 'AM';

        // 0 hours should display as 12
        if (hour12 === 0) {
            hour12 = 12;
        }

        // Include minutes if they are not "00"
        const formattedTime = minutes !== '00'
            ? `${hour12}:${minutes} ${period}`
            : `${hour12} ${period}`;

        return formattedTime;
    } catch (error) {
        console.error('Error converting time format:', error);
        return timeString;
    }
};

// input '1 AM'  ->  output '01:00 AM'
export const formatHourLabel = (input) => {
    if (!input) return '';

    const [hourStr, period] = input.trim().split(' ');
    let hour = parseInt(hourStr, 10);

    if (isNaN(hour) || !period) return input;

    const paddedHour = hour.toString().padStart(2, '0');

    return `${paddedHour}:00 ${period.toUpperCase()}`;
}

export const parse12HourTimeToMinutes = (value) => {
    if (typeof value !== 'string') return null;

    const match = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(value.trim());
    if (!match) return null;

    const hour = Number(match[1]);
    const minute = Number(match[2]);
    if (hour < 1 || hour > 12 || minute < 0 || minute > 59) return null;

    const normalizedHour = hour % 12 + (match[3].toUpperCase() === 'PM' ? 12 : 0);
    return normalizedHour * 60 + minute;
};

// input '12 AM' -> 0, '1 AM' -> 1, '12 PM' -> 12, '11 PM' -> 23; invalid -> null
export const hourLabelToIndex = (label) => {
    if (typeof label !== 'string') return null;

    const match = /^(\d{1,2})\s*(AM|PM)$/i.exec(label.trim());
    if (!match) return null;

    const hour = Number(match[1]);
    if (hour < 1 || hour > 12) return null;

    return hour % 12 + (match[2].toUpperCase() === 'PM' ? 12 : 0);
};

// input ['12 AM', '1 AM', '11 PM', '10 PM'] -> output [0, 1, 22, 23]
// Sorted ascending; invalid labels and duplicates are dropped.
export const hourLabelsToIndexes = (labels) => {
    if (!Array.isArray(labels)) return [];

    const indexes = labels
        .map(hourLabelToIndex)
        .filter((index) => index !== null);

    return [...new Set(indexes)].sort((a, b) => a - b);
};

// input '0,7,8,23' -> output [0, 7, 8, 23]   (the reverse of hourLabelsToIndexes(...).join(','))
// Sorted ascending; blanks, non-numbers, values outside 0-23 and duplicates are dropped.
// '' / null / undefined -> []
export const parseHourIndexes = (value) => {
    if (value === null || value === undefined) return [];

    const indexes = String(value)
        .split(',')
        .map((part) => part.trim())
        .filter((part) => /^\d{1,2}$/.test(part))
        .map(Number)
        .filter((hour) => hour <= 23);

    return [...new Set(indexes)].sort((a, b) => a - b);
};

export const compareHourValues = (firstValue, secondValue, direction) => {
    const firstMinutes = parse12HourTimeToMinutes(firstValue);
    const secondMinutes = parse12HourTimeToMinutes(secondValue);
    const firstIsInvalid = firstMinutes === null;
    const secondIsInvalid = secondMinutes === null;

    if (firstIsInvalid && secondIsInvalid) return 0;
    if (firstIsInvalid) return 1;
    if (secondIsInvalid) return -1;

    const comparison = firstMinutes - secondMinutes;
    return direction === 'desc' ? -comparison : comparison;
};

export const normalizeSiteScheduleTime = (value) => {
    if (!value) return '';

    const trimmedValue = String(value).trim();

    if (/^\d{1,2}:\d{2}\s*(AM|PM)$/i.test(trimmedValue) || /^\d{1,2}\s*(AM|PM)$/i.test(trimmedValue)) {
        return trimmedValue.replace(/\s+/g, ' ').toUpperCase();
    }

    const timeMatch = trimmedValue.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (!timeMatch) {
        return trimmedValue;
    }

    const hours = Number(timeMatch[1]);
    const minutes = timeMatch[2];

    if (hours < 0 || hours > 23) {
        return trimmedValue;
    }

    let hour12 = hours % 12;
    const period = hours >= 12 ? 'PM' : 'AM';

    if (hour12 === 0) {
        hour12 = 12;
    }

    return minutes !== '00'
        ? `${hour12}:${minutes} ${period}`
        : `${hour12} ${period}`;
};

export const getTimeSortValue = (value) => {
    if (typeof value !== 'string') return -1;

    const match = /(\d{1,2}):(\d{2})/.exec(value.trim());
    if (!match) return -1;

    const hour = Number(match[1]);
    const minute = Number(match[2]);
    if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return -1;

    return hour * 60 + minute;
};

export const getTimezoneDetails = (tz, fullSrting = true) => {
    if (fullSrting) {
        return `Time zone in ${tz}`
    } else {
        return `(${tz})`
    }
}

// Tenant TimeZoneCode (Master_TimeZone.TimeZoneCode) -> IANA zone, mirrors backend TimeZoneHelper.GetCorrectTimeZone.
export const TIMEZONE_CODE_TO_IANA = {
    CT: 'America/Chicago',
    ET: 'America/New_York',
    MT: 'America/Denver',
    AT: 'America/Phoenix',
    PT: 'America/Los_Angeles',
    HT: 'Pacific/Honolulu',
    IST: 'Asia/Kolkata',
    JST: 'Asia/Tokyo',
    UTC: 'UTC',
};

export const DEFAULT_TENANT_TIMEZONE_CODE = 'CT';

// Tenant TimeZoneCode cached for non-React code (sagas, fileDownloads, dateUtils) that cannot read the store.
let currentTenantTimeZoneCode = DEFAULT_TENANT_TIMEZONE_CODE;

// Set from the GetTenantTimeZone response (and the public scheduling link Validate response).
export const setTenantTimeZoneCode = (tzCode) => {
    currentTenantTimeZoneCode = `${tzCode || DEFAULT_TENANT_TIMEZONE_CODE}`.trim().toUpperCase();
};

/**
 * Current time as wall-clock in the tenant zone (UTC instant converted to tenant time), independent of the browser zone.
 * Read it with getFullYear/getMonth/getDate/getHours/getMinutes/getSeconds or wrap it with moment().
 * @param {string} tzCode - Tenant TimeZoneCode (e.g. "CT"); falls back to the cached tenant code, then CT
 * @returns {Date}
 */
export const getTenantNowDate = (tzCode) => {
    const timeZone = TIMEZONE_CODE_TO_IANA[getTenantTimeZoneCode(tzCode)];

    try {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone,
            hourCycle: 'h23',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        }).formatToParts(new Date());
        const part = (type) => Number(parts.find((item) => item.type === type)?.value || 0);

        return new Date(part('year'), part('month') - 1, part('day'), part('hour'), part('minute'), part('second'));
    } catch (error) {
        return new Date();
    }
};

// Normalized tenant TimeZoneCode for labels (e.g. "CT"); empty uses the cached tenant code, unknown falls back to CT.
export function getTenantTimeZoneCode(tzCode) {
    const code = `${tzCode || currentTenantTimeZoneCode || DEFAULT_TENANT_TIMEZONE_CODE}`.trim().toUpperCase();
    return TIMEZONE_CODE_TO_IANA[code] ? code : DEFAULT_TENANT_TIMEZONE_CODE;
}

/**
 * Common export file-name date/time in tenant time: "10-12-26_(01-47 AM CT)" (MM-DD-YY_(hh-mm AM/PM CODE)).
 * @param {string} tzCode - Tenant TimeZoneCode; defaults to the cached tenant code
 * @returns {string}
 */
export const getExportDateTimeLabel = (tzCode) => {
    const code = getTenantTimeZoneCode(tzCode);
    const now = getTenantNowDate(code);
    const pad = (value) => `${value}`.padStart(2, '0');
    const hours24 = now.getHours();

    return `${pad(now.getMonth() + 1)}-${pad(now.getDate())}-${`${now.getFullYear()}`.slice(-2)}`
        + `_(${pad(hours24 % 12 || 12)}-${pad(now.getMinutes())} ${hours24 >= 12 ? 'PM' : 'AM'} ${code})`;
};

/**
 * Common export file name: "<name>_10-12-26_(01-47 AM CT).xlsx".
 * @param {string} name - File name prefix
 * @param {string} ext - Extension without dot (default "xlsx")
 * @param {string} tzCode - Tenant TimeZoneCode; defaults to the cached tenant code
 * @returns {string}
 */
export const buildExportFileName = (name, ext = 'xlsx', tzCode) => `${name}_${getExportDateTimeLabel(tzCode)}.${ext}`;
